import { z } from 'zod'

// === Folder/File Types ===

export const googleDriveFolderItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  mime_type: z.string().default('application/vnd.google-apps.folder'),
})

export type GoogleDriveFolderItem = z.infer<typeof googleDriveFolderItemSchema>

export const googleDriveFolderListResponseSchema = z.object({
  folders: z.array(googleDriveFolderItemSchema),
  parent_id: z.string().nullable(),
  parent_name: z.string().nullable(),
})

export type GoogleDriveFolderListResponse = z.infer<typeof googleDriveFolderListResponseSchema>

export const googleDriveFileItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  mime_type: z.string(),
  size: z.number(),
  modified_time: z.string(),
})

export type GoogleDriveFileItem = z.infer<typeof googleDriveFileItemSchema>

export const googleDriveFilePreviewResponseSchema = z.object({
  files: z.array(googleDriveFileItemSchema),
  total_count: z.number(),
  has_more: z.boolean().default(false),
})

export type GoogleDriveFilePreviewResponse = z.infer<typeof googleDriveFilePreviewResponseSchema>

// === Sync Configuration ===

export const syncFrequencySchema = z.enum(['manual', 'daily', 'weekly'])

export type SyncFrequency = z.infer<typeof syncFrequencySchema>

export const syncStatusSchema = z.enum(['idle', 'pending', 'syncing', 'error'])

export type SyncStatus = z.infer<typeof syncStatusSchema>

export const googleDriveSyncConfigRequestSchema = z.object({
  enabled: z.boolean().default(true),
  folder_id: z.string().min(1, 'Pasta obrigatoria'),
  folder_name: z.string().min(1, 'Nome da pasta obrigatorio'),
  folder_path: z.string().nullable().optional(),
  frequency: syncFrequencySchema.default('manual'),
  file_types: z.array(z.string()).default(['pdf', 'docx', 'txt', 'md']),
})

export type GoogleDriveSyncConfigRequest = z.infer<typeof googleDriveSyncConfigRequestSchema>

export const googleDriveSyncConfigResponseSchema = z.object({
  enabled: z.boolean(),
  folder_id: z.string().nullable(),
  folder_name: z.string().nullable(),
  folder_path: z.string().nullable(),
  frequency: z.string(),
  file_types: z.array(z.string()),
  last_sync_at: z.string().nullable(),
  sync_status: z.string(),
})

export type GoogleDriveSyncConfigResponse = z.infer<typeof googleDriveSyncConfigResponseSchema>

export const googleDriveSyncTriggerResponseSchema = z.object({
  message: z.string(),
  status: z.string(),
})

export type GoogleDriveSyncTriggerResponse = z.infer<typeof googleDriveSyncTriggerResponseSchema>

// === UI Options ===

export const SYNC_FREQUENCY_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'daily', label: 'Diariamente' },
  { value: 'weekly', label: 'Semanalmente' },
] as const

export const FILE_TYPE_OPTIONS = [
  { value: 'pdf', label: 'PDF', extension: '.pdf' },
  { value: 'docx', label: 'Word (DOCX)', extension: '.docx' },
  { value: 'doc', label: 'Word (DOC)', extension: '.doc' },
  { value: 'txt', label: 'Texto (TXT)', extension: '.txt' },
  { value: 'md', label: 'Markdown (MD)', extension: '.md' },
  { value: 'csv', label: 'CSV', extension: '.csv' },
  { value: 'xlsx', label: 'Excel (XLSX)', extension: '.xlsx' },
  { value: 'pptx', label: 'PowerPoint (PPTX)', extension: '.pptx' },
] as const

export const DEFAULT_FILE_TYPES = ['pdf', 'docx', 'txt', 'md']

// === Helper Functions ===

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export function formatSyncDate(dateString: string | null): string {
  if (!dateString) return 'Nunca sincronizado'
  const date = new Date(dateString)
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function getSyncStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    idle: 'Disponivel',
    pending: 'Aguardando',
    syncing: 'Sincronizando',
    error: 'Erro',
  }
  return labels[status] || status
}

export function getSyncStatusColor(status: string): 'default' | 'secondary' | 'destructive' {
  const colors: Record<string, 'default' | 'secondary' | 'destructive'> = {
    idle: 'secondary',
    pending: 'default',
    syncing: 'default',
    error: 'destructive',
  }
  return colors[status] || 'secondary'
}
